import { useState } from 'react';

import MessageIcon from '@mui/icons-material/Message';
import { useRecordModal } from 'hooks/useRecordModal';
import { useGetOrderAdjustments } from 'pages/Orders/hooks/useGetOrderAdjustments';
import {
  StyledCard,
  CardHeaderStyled,
  AlertStyled,
} from 'pages/Orders/OrdersShow/OrdersShowCommonInfo/OrdersShowCommonInfo.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import {
  ListContextProvider,
  RecordContextProvider,
  useList,
  useShowContext,
  WithListContext,
} from 'react-admin';
import { CancelButton } from 'shared/mui/Buttons';
import { Collapse } from 'shared/mui/Collapse/Collapse';
import { BaseDialog } from 'shared/mui/Dialogs';
import { ExpandIconButton, IconButton } from 'shared/mui/IconButtons';
import {
  AdjustmentAmountField,
  AdjustmentChangeStatusField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  TextField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { AdjustmentStatusChipField } from 'shared/react-admin/Fields/AdjustmentStatusChipField/AdjustmentStatusChipField';
import { ActionsSlots } from 'shared/react-admin/Table/ActionsSlots/ActionsSlots';
import { Order } from 'types/embedded/order';
import { OrderAdjustments } from 'types/embedded/orderAdjustments';
import { User } from 'types/embedded/user';

import { StyledDatagridWithStickyActions } from './OrdersShowCommonInfoAdjustments.styled';

export const OrdersShowCommonInfoAdjustments = (): JSX.Element => {
  const translate = useTypedTranslate();
  const { record } = useShowContext<Order>();
  const { data, isFetching } = useGetOrderAdjustments(record?.originId);
  const context = useList({ data, isFetching });
  const {
    record: recordCommentDialog,
    handleOpenRecordModal: handleOpenCommentDialog,
    openRecordModal: openCommentDialog,
    handleCloseRecordModal: handleCloseCommentDialog,
  } = useRecordModal<OrderAdjustments>();

  const [isExpanded, setIsExpanded] = useState(true);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <StyledCard>
      <ListContextProvider value={context}>
        <CardHeaderStyled
          appendActions={
            <ExpandIconButton isExpanded={isExpanded} onClick={handleExpandClick} size="small" />
          }
          isExpanded={isExpanded}
          title={translate('app.orders.orderAdjustments')}
          titleSize="smallBold"
        />
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <WithListContext
            render={({ data, isFetching }) => {
              if (!data?.length && !isFetching) {
                return (
                  <AlertStyled severity="info">
                    {translate('app.orders.noOrderAdjustments')}
                  </AlertStyled>
                );
              }
              return (
                <StyledDatagridWithStickyActions bulkActionButtons={false}>
                  <TextField
                    label={translate('app.orders.adjustmentId')}
                    sortable={false}
                    source="id"
                  />
                  <TextField
                    label={translate('app.orders.adjustmentType')}
                    source="adjustmentType.description"
                  />
                  <FunctionField
                    label={translate('app.orders.playerBalanceTransactions')}
                    render={(record: OrderAdjustments) =>
                      `${record.impactToBalance ? translate('app.common.yes') : translate('app.common.no')}`
                    }
                    source="impactToBalance"
                  />
                  <FunctionField
                    label={translate('app.orders.adjustmentStatus')}
                    render={(record: OrderAdjustments) => (
                      <RecordContextProvider value={record.adjustmentStatus}>
                        <AdjustmentStatusChipField source="description" />
                      </RecordContextProvider>
                    )}
                  />
                  <AdjustmentAmountField
                    label={translate('app.orders.amountChange')}
                    source="amount"
                  />
                  <AdjustmentChangeStatusField
                    label={translate('app.orders.statusChange')}
                    source="change-status"
                  />
                  <EmbeddedFunctionField
                    cellClassName="column-user"
                    label={translate('app.orders.initiatorAuthor')}
                    render={(record: User) => (
                      <RecordContextProvider value={record}>
                        <UserProfileField />
                      </RecordContextProvider>
                    )}
                    sortable={false}
                    sourceField="user"
                  />
                  <DateTimeField label={translate('app.common.createdAt')} source="createdAt" />
                  <DateTimeField label={translate('app.common.updatedAt')} source="updatedAt" />
                  <ActionsSlots
                    render={(record: OrderAdjustments) => {
                      return (
                        <IconButton
                          disabled={!record?.comment}
                          onClick={() => handleOpenCommentDialog(record)}
                        >
                          <MessageIcon color={record?.comment ? 'primary' : 'secondary'} />
                        </IconButton>
                      );
                    }}
                  />
                </StyledDatagridWithStickyActions>
              );
            }}
          />
        </Collapse>
        {recordCommentDialog && (
          <BaseDialog
            message={recordCommentDialog?.comment}
            onClose={handleCloseCommentDialog}
            open={openCommentDialog}
            rightActionsSlot={[
              <CancelButton
                key="cancel-button"
                label={translate('app.buttons.close')}
                onClick={handleCloseCommentDialog}
                variant="contained"
              />,
            ]}
            title={translate('app.orders.commentOper', { originId: recordCommentDialog?.id })}
          />
        )}
      </ListContextProvider>
    </StyledCard>
  );
};
